import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { faBackward, faBullseye, faListOl, faLongArrowAltLeft, faSearch, faThumbsUp, faTimes } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ActionButton, HeadingItem, SearchBox } from 'components';
import { BarcodeSearch } from 'components/barcode';
import { SearchResultList } from 'components/item-verification';
import { FilteredLps } from 'components/item-verification/filtered-lps/filtered-lps';
import { ConfirmationPopup, ReturnAuthorizationPopup } from 'components/pop-up';
import { TripContext } from 'contexts';
import { ItemVerificationView, ScanResultValueType, SearchResultType } from 'enums';
import React, { Fragment, useContext, useState } from 'react';
import { useLicensePlateService, useNavigation, useOrderItemService, useParamService, useReturnItemService } from 'services';
import { Temperature, VerifiedMethod } from 'services/enums';
import { CatchWeight, FilteredLicensePlate, LicensePlate, LicensePlateItem, LineItem, Order, OrderItem, OrderItemChange, OrderLicensePlate, ReturnItem, Stop } from 'services/types';
import { ScanResult, ScanResultValue, SearchResult, SelectedItem } from 'types';
import { SearchResultHelper } from 'utils';
import './item-verification.scss';
import notify from 'devextreme/ui/notify';

export const ItemVerificationPage = () => {

    const { navigateTo, navigateBack } = useNavigation()
    const { getIntValue, getStringValue } = useParamService
    const { verifyReturnItem } = useReturnItemService()
    const { verifyItem, verifyItems, approveOrderItemChange } = useOrderItemService()
    const { areAllLpItemsVerified } = useLicensePlateService()
    const { isOrderItemChangeComplete } = useOrderItemService()
    const tripContext = useContext(TripContext)

    const stopId: number | undefined = getIntValue('stopId')
    const searchType: ItemVerificationView | undefined = getIntValue('searchId')
    const poNumber: string | undefined = getStringValue('poNumber')

    const [searchString, setSearchString] = useState<string>('')
    const [isPopupVisible, setPopupVisible] = useState<boolean>(false)
    const [isConfirmPopupVisible, setConfirmPopupVisible] = useState<boolean>(false)

    const temperatures: SelectedItem<Temperature>[] = ([
        { item: Temperature.Dry, isSelected: (getStringValue('dry') == 'true') },
        { item: Temperature.Frozen, isSelected: (getStringValue('frozen') == 'true') },
        { item: Temperature.Refrigerated, isSelected: (getStringValue('ref') == 'true') }
    ])

    const stop: Stop = tripContext.stops.find(stop => stop.id == stopId) ?? {} as Stop
    const orders: Order[] = tripContext.orders.filter(lp => lp.stopId === stopId) ?? []
    const stopItems: OrderItem[] = tripContext.orderItems.filter(item => orders.some(order => order.id === item.orderId))
    const stopCatchWeights: CatchWeight[] = tripContext.catchWeights.filter(cw => stopItems.some(item => item.id === cw.orderItemId))
    const lineItems: LineItem[] = tripContext.lineItems.toArray() ?? []
    const stopLps: LicensePlate[] = tripContext.licensePlates.filter(lp => lp.stopId == stopId) ?? []
    const orderItemsChanges: OrderItemChange[] = tripContext.orderItemChanges.filter(change => stopItems.some(orderItem => orderItem.id == change.orderItemId))
    const returnItems: ReturnItem[] = tripContext.returnItems.filter(returnItem => orders.some(order => order.id === returnItem.orderId))
        .sort((a, b) => { return (a.isChangeComplete == b.isChangeComplete) ? 0 : a.isChangeComplete ? 1 : -1 })

    const temperatureItems: OrderItem[] = stopItems.filter(orderItem => temperatures.some(temp => temp.item === lineItems.find(lineItem => lineItem.id == orderItem.lineItemId)?.temperature && temp.isSelected))
    const temperatureReturnItems: ReturnItem[] = returnItems.filter(ri => temperatures.some(temp => temp.item == lineItems.find(lineItem => lineItem.id == ri.lineItemId)?.temperature && temp.isSelected))
    const { notVerifiedItems, shortedItems, changesItems, verifiedItems } = SearchResultHelper.categorizeOrderItems(temperatureItems, orderItemsChanges, stopCatchWeights)
    const lps = stopLps.filter(lp => temperatures.some(temp => temp.item === lp.temperature && temp.isSelected))
    const { notVerifiedResults, changesResults, shortedResults, allResults } = SearchResultHelper.convertOrderItemsToSearchResults(notVerifiedItems, shortedItems, changesItems, verifiedItems, orderItemsChanges)
    const licensePlateResults: SearchResult<LicensePlate>[] = lps.map(lp => { return { type: SearchResultType.LicensePlate, value: lp } })
    const returnItemResults: SearchResult<ReturnItem>[] = temperatureReturnItems.map(returnItem => { return { type: SearchResultType.ReturnItem, value: returnItem } })
    let results: SearchResult<OrderItem | LicensePlate | ReturnItem>[] = []
    const filteredLicensePlates: FilteredLicensePlate[] = tripContext.filteredLicensePlates.filter(flp => flp.stopId == stopId) ?? []
    const filteredLps: LicensePlate[] = tripContext.licensePlates.filter(lp => filteredLicensePlates.some(flp => flp.id === lp.id))
    const tripId: number | undefined = stop?.tripId
    const orderLps: OrderLicensePlate[] = tripContext.orderLicensePlates.filter(lpo => filteredLicensePlates.some(lp => lp.id == lpo.licensePlateId))
    const lpItems: LicensePlateItem[] = tripContext.licensePlateItems.filter(lpi => filteredLicensePlates.some(lp => lp.id == lpi.licensePlateId))
    const lpOrders: Order[] = tripContext.orders.filter(o => orderLps.some(olp => olp.orderId == o.id))
    const filteredByLpOrderItems: OrderItem[] = notVerifiedItems.filter((oi: OrderItem) => lpOrders.some(o => o.id == oi.orderId) && lpItems.some(lpi => lpi.lineItemId == oi.lineItemId)) ?? []
    const filteredByLp: SearchResult<OrderItem>[] = filteredByLpOrderItems.map(item => { return { type: SearchResultType.NotVerifiedItem, value: item } })

    const handleResultSelected = async (result: SearchResult<OrderItem | LicensePlate | ReturnItem>) => {
        if (result?.type === SearchResultType.LicensePlate) {
            await navigateTo(`lp-details?lpId=${result.value.id}`)
        }
        else if (result?.type === SearchResultType.ReturnItem) {
            await verifyReturnItem(returnItems.find(item => item.id == result.value.id)!, VerifiedMethod.Manual)
            await navigateTo(`verify-returnitem?stopId=${stopId}&returnItemId=${result.value.id}`)
        }
    }

    const onVerifyClicked = async (orderItem: OrderItem) => {
        if (areAllLpItemsVerified(stopLps, orderItem.lineItemId)) {
            await verifyItem(orderItem, VerifiedMethod.Manual)
        }
        if (searchType == ItemVerificationView.PoDetails) {
            await navigateTo(`verify-item?stopId=${stopId}&itemId=${orderItem.lineItemId}`, undefined, '/stop-overview')
        }
        else {
            await navigateTo(`verify-item?stopId=${stopId}&itemId=${orderItem.lineItemId}`)
        }
    }

    const onApproveChangeClicked = async (orderItem: OrderItem) => {
        const notCompletChanges: OrderItemChange[] = (orderItemsChanges && orderItemsChanges.filter(x => !isOrderItemChangeComplete(x))) ?? []
        notCompletChanges && notCompletChanges.length > 0 ? notify(`Please complete all necessary item change info`, 'info', 5000) : approveOrderItemChange(orderItem)
    }

    const clearAllFilteredLps = () => {
        const lpsToDelete = tripContext.filteredLicensePlates.toArray()
        const lpIds: number[] = lpsToDelete ? lpsToDelete.map(lp => lp.id!) : []
        tripContext.filteredLicensePlates.bulkRemove(lpIds)
        setConfirmPopupVisible(false)
    }

    const removeFilteredLps = async (licensePlate: FilteredLicensePlate) => {
        const lpsToDelete = tripContext.filteredLicensePlates.find(lp => lp.id == licensePlate.id && lp.stopId == licensePlate.stopId)
        lpsToDelete && lpsToDelete.id && tripContext.filteredLicensePlates.remove(lpsToDelete.id)
        setConfirmPopupVisible(false)
    }

    const changesShortedItemsResults = [...changesResults, ...shortedResults].filter(x => x.value.customerPoNumber === poNumber);
    
    const changesShortedItemsPoDetails: SearchResult<OrderItem>[] = [];
    changesShortedItemsResults.forEach(result => {
        if (!changesShortedItemsPoDetails.some(change => change.value.lineItemId === result.value.lineItemId)) {
            changesShortedItemsPoDetails.push(result);
        }
    });

    switch (searchType) {
        case ItemVerificationView.NotVerified: results = filteredLicensePlates.length == 0 ? notVerifiedResults : filteredByLp; break;
        case ItemVerificationView.Changes: results = changesResults; break;
        case ItemVerificationView.ShortedItems: results = shortedResults; break;
        case ItemVerificationView.LPs: results = licensePlateResults; break;
        case ItemVerificationView.Returns: results = returnItemResults; break;
        case ItemVerificationView.PoDetails: results = changesShortedItemsPoDetails; break;
        case ItemVerificationView.Search:
        default: results = [...allResults, ...licensePlateResults, ...returnItemResults]; break;
    }

    const onItemScanned = async (result: ScanResult): Promise<void> => {
        const returnItemValue: ScanResultValue = result.values.find(value => value.type == ScanResultValueType.ReturnItem) ?? {} as ScanResultValue
        const orderItemValues: ScanResultValue[] = result.values.filter(value => value.type == ScanResultValueType.OrderItem) ?? []

        if (searchType == ItemVerificationView.Returns) {
            const item: ReturnItem = returnItemValue.value as ReturnItem
            await verifyReturnItem(item, VerifiedMethod.Scanned)
            await navigateTo(`verify-returnitem?stopId=${stopId}&returnItemId=${item.id}`)
        }
        else {
            const items: OrderItem[] = orderItemValues.map(item => { return item.value as OrderItem })
            if (areAllLpItemsVerified(stopLps, items[0].lineItemId)) {
                await verifyItems(items, VerifiedMethod.Scanned)
            }
            let order: Order | undefined = orders.find(o => o.id === items[0].orderId)
            if (order?.signatureId == null) {
                await navigateTo(`verify-item?stopId=${stopId}&itemId=${items[0].lineItemId}`)
            } else {
                await navigateTo(`item-details?orderItemId=${items[0].id}`)
            }
        }
    }

    const haveFilteredLps: boolean = (searchType == ItemVerificationView.NotVerified && filteredLps.length > 0)

    return (
        <Fragment>
            <BarcodeSearch tripId={tripId} stopId={stopId} onItemScanned={onItemScanned} />
            { (searchType == ItemVerificationView.Search || results.length > 0) &&
                <div className={'item-list-heading-card'}>
                    <div className={'item-list-heading-card-leftcoloumn'}>
                        <SearchBox placeHolder={'Search'} searchQuery={searchString} onSearch={query => setSearchString(query)} />
                    </div>
                    <div className={'item-list-clear-card'} onClick={() => setSearchString('')}>
                        <FontAwesomeIcon icon={faTimes as IconProp} color='black' size='2x' />
                    </div>
                </div>
            }
            <div className={'item-verification-scannedlp-container'}>
                {(searchType == ItemVerificationView.NotVerified && filteredLps.length > 0) &&
                    <FilteredLps
                        licensePlates={filteredLps}
                        onClickClearAll={() => setConfirmPopupVisible(true)}
                        onClickRemoveLp={removeFilteredLps} />
                }
            </div>
            <div className={`item-verification-search-resultlist`}>
                <SearchResultList
                    visible={results.length > 0}
                    results={results}
                    searchString={searchString}
                    onResultSelected={handleResultSelected}
                    onDetailsClicked={async (orderItem: OrderItem) => await navigateTo(`item-details?orderItemId=${orderItem.id}&searchId=${searchType}`)}
                    onVerifyClicked={onVerifyClicked}
                    onApproveChangeClicked={onApproveChangeClicked}
                />
                {(searchType == ItemVerificationView.NotVerified && results.length === 0) && 
                    <div className={'card-material'}>
                        <HeadingItem text={'All Filtered Items Verified'}/>
                        <div className={'item-verification-no-results-message'}>All your items are verified. Would you like to search for something?</div>
                        <div className={'item-verification-no-results-buttons-container'}>
                            <ActionButton icon={faLongArrowAltLeft} buttonText={'Go Back'} buttonColor={'#3498db'} onClick={() => navigateBack()} />
                            <ActionButton icon={faSearch} buttonText={'Search'} buttonColor={'#f2994a'} onClick={() => navigateTo(`item-verification?stopId=${stopId}&searchId=${ItemVerificationView.Search}&dry=${temperatures[0].isSelected}&frozen=${temperatures[1].isSelected}&ref=${temperatures[2].isSelected}`, 'Search')} />
                        </div>
                    </div>
                }
            </div>
            {searchType == ItemVerificationView.Returns &&
                <div className={'bottom-fix-action-row'}>
                    <ActionButton
                        icon={faListOl}
                        buttonColor={'#F2994A'}
                        onClick={() => setPopupVisible(true)}
                        buttonText={'RAs'}
                    />
                </div>
            }
            <ReturnAuthorizationPopup
                isVisible={isPopupVisible}
                title="Return Authorizations"
                stopId={stopId!}
                onClickCloseButton={() => setPopupVisible(false)}
            />
            <ConfirmationPopup
                isVisible={isConfirmPopupVisible}
                title="Warning"
                message={`Are you sure you want to clear all the filters?`}
                button1={{ icon: faThumbsUp, text: "OK", backgroundColor: '#525252' }}
                onClickButton1={() => clearAllFilteredLps()}
                isCloseButtonVisible={true}
                onClickCloseButton={() => setConfirmPopupVisible(false)}
            />
        </Fragment>
    )
};
